import React, { useState, useEffect, useCallback } from 'react';
import './CondolenceList.css';

function CondolenceList({ condolences }) {
  const [gallery, setGallery] = useState(null); // { photos: [], currentIndex: 0 }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const openGallery = (photos, startIndex = 0) => {
    setGallery({ photos, currentIndex: startIndex });
  };

  const closeGallery = () => {
    setGallery(null);
  };

  const goToPrevious = useCallback(() => {
    if (!gallery) return;
    setGallery(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === 0 ? prev.photos.length - 1 : prev.currentIndex - 1
    }));
  }, [gallery]);

  const goToNext = useCallback(() => {
    if (!gallery) return;
    setGallery(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === prev.photos.length - 1 ? 0 : prev.currentIndex + 1
    }));
  }, [gallery]);

  // Keyboard navigation
  useEffect(() => {
    if (!gallery) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeGallery();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gallery, goToPrevious, goToNext]);

  // Get display name (respects anonymous setting)
  const getDisplayName = (condolence) => {
    return condolence.isAnonymous ? 'Anonymous' : condolence.fullName;
  };

  // Get avatar initial
  const getAvatarInitial = (condolence) => {
    return condolence.isAnonymous ? '?' : condolence.fullName.charAt(0).toUpperCase();
  };

  return (
    <div className="condolence-list">
      {condolences.map((condolence) => {
        const displayName = getDisplayName(condolence);
        
        return (
          <article key={condolence._id} className="condolence-card">
            <header className="condolence-header">
              <div className={`condolence-avatar ${condolence.isAnonymous ? 'anonymous' : ''}`}>
                {getAvatarInitial(condolence)}
              </div>
              <div className="condolence-meta">
                <h3 className={`condolence-name ${condolence.isAnonymous ? 'anonymous' : ''}`}>
                  {displayName}
                </h3>
                <time className="condolence-date">
                  {formatDate(condolence.submittedAt)}
                </time>
              </div>
            </header>

            {condolence.message && (
              <p className="condolence-message">{condolence.message}</p>
            )}

            {condolence.photos && condolence.photos.length > 0 && (
              <div className={`condolence-photos photos-${Math.min(condolence.photos.length, 4)}`}>
                {condolence.photos.slice(0, 4).map((photo, index) => (
                  <div 
                    key={index} 
                    className="photo-thumbnail"
                    onClick={() => openGallery(condolence.photos, index)}
                  >
                    <img 
                      src={photo.url} 
                      alt={`Shared memory`}
                      loading="lazy"
                    />
                    {index === 3 && condolence.photos.length > 4 && (
                      <div className="more-photos-overlay">
                        +{condolence.photos.length - 4}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </article>
        );
      })}

      {/* Photo Gallery Modal */}
      {gallery && (
        <div className="image-modal" onClick={closeGallery}>
          <button className="modal-close" aria-label="Close">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          {/* Navigation arrows - only show if more than 1 photo */}
          {gallery.photos.length > 1 && (
            <>
              <button 
                className="gallery-nav gallery-prev" 
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                aria-label="Previous photo"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button 
                className="gallery-nav gallery-next" 
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                aria-label="Next photo"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </>
          )}

          <img 
            src={gallery.photos[gallery.currentIndex].url} 
            alt="Expanded view"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Photo counter */}
          {gallery.photos.length > 1 && (
            <div className="gallery-counter">
              {gallery.currentIndex + 1} / {gallery.photos.length}
            </div>
          )}

          {/* Thumbnail strip for quick navigation */}
          {gallery.photos.length > 1 && (
            <div className="gallery-thumbnails" onClick={(e) => e.stopPropagation()}>
              {gallery.photos.map((photo, index) => (
                <button
                  key={index}
                  className={`gallery-thumb ${index === gallery.currentIndex ? 'active' : ''}`}
                  onClick={() => setGallery(prev => ({ ...prev, currentIndex: index }))}
                >
                  <img src={photo.url} alt={`Thumbnail ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CondolenceList;


